import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { PrivilegeIdLendingRecordState, PrivilegeIdLendingRecordComment } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import router from '../../../router';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<PrivilegeIdLendingRecordState, RootState> = {
    /**
     * 特権ID貸出記録登録(PW・鍵取得)のエラー等のクリア
     * 登録に失敗した場合などに、そのままのエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_LENDING_RECORDS_ERROR);
    },

    /**
     * 特権ID貸出履歴一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.SEARCH,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 特権ID貸出履歴の詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 貸出履歴ID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索絞り込み項目
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { searchItems = {}, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.PrivilegeIdLendingRecord.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CSV_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 動画情報の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 特権ID貸出記録ID
     * @returns {Promise<void>}
     */
    async movieFile({ dispatch, commit }, { id }) {
        try {
            commit(types.MOVIE_FILE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.MOVIE_FILE.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.MOVIE_FILE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.MOVIE_FILE_FAILURE, params);
        }
    },

    /**
     * 検知情報の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 特権ID貸出記録ID
     * @returns {Promise<void>}
     */
    async textFile({ dispatch, commit }, { id }) {
        try {
            commit(types.TEXT_FILE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.TEXT_FILE.replace(':id', id.toString()),
                    params: { detection_limit: AppContext.DetectionLogs.MAX_ITEMS },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.TEXT_FILE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.TEXT_FILE_FAILURE, params);
        }
    },

    /**
     * RECファイル情報の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 特権ID貸出記録ID
     * @returns {Promise<void>}
     */
    async recFile({ dispatch, commit }, { id }) {
        try {
            commit(types.REC_FILE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.REC_FILE.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REC_FILE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REC_FILE_FAILURE, params);
        }
    },

    /**
     * 特権ID貸出記録登録(PW・鍵取得)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} id 申請書作業アカウントID (申請書の作業毎に紐付いたノード×ノードアカウント(作業一覧の各行)毎に割り当てられたID)
     * @param {any} newPrivilegeIdLendingRecords 特権ID貸出記録登録(PW・鍵取得)情報
     * @returns {Promise<void>}
     */
    async privilegeIdLendingRecords({ dispatch, commit, rootState }, { id, newPrivilegeIdLendingRecords } = {}) {
        try {
            commit(types.PRIVILEGE_ID_LENDING_RECORDS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.ApplicationFormWorkAccounts.PRIVILEGE_ID_LENDING_RECORDS.replace(':id', id),
                    params: newPrivilegeIdLendingRecords,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.PRIVILEGE_ID_LENDING_RECORDS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.PRIVILEGE_ID_LENDING_RECORDS_FAILURE, params);
        }
    },

    /**
     * 定常作業の特権ID貸出記録登録(PW・鍵取得)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} newRoutineWork 特権ID貸出記録登録(PW・鍵取得)情報
     * @returns {Promise<void>}
     */
    async routineWork({ dispatch, commit }, newRoutineWork) {
        try {
            commit(types.ROUTINE_WORK_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.PrivilegeIdLendingRecord.ROUTINE_WORK,
                    params: newRoutineWork,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ROUTINE_WORK_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ROUTINE_WORK_FAILURE, params);
        }
    },

    /**
     * 特権ID貸出履歴画面を閲覧可能かどうかの情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async displayable({ dispatch, commit, rootState }) {
        try {
            commit(types.DISPLAYABLE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.PrivilegeIdLendingRecord.DISPLAYABLE,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DISPLAYABLE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DISPLAYABLE_FAILURE, params);
        }
    },

    /**
     * RECファイルダウンロード処理開始
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async recFileDownloadStart({ commit }) {
        commit(types.REC_FILE_DOWNLOAD_START);
    },

    /**
     * RECファイルダウンロード処理正常終了
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async recFileDownloadSuccess({ commit }) {
        commit(types.REC_FILE_DOWNLOAD_SUCCESS);
    },

    /**
     * RECファイルダウンロード処理エラー終了
     * @param {any} commit
     * @param resData エラーレスポンス
     * @returns {Promise<void>}
     */
    async recFileDownloadFailure({ commit }, resData) {
        const params = {
            errorTitle: resData.title,
            errorDetails: resData.errors,
        };
        commit(types.REC_FILE_DOWNLOAD_FAILURE, params);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },

    /**
     * 特権ID貸出履歴に紐付いたコメントの更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {PrivilegeIdLendingRecordComment} privilegeIdLendingRecordComment 特権ID貸出記録IDとそのIDに紐付いたコメント
     * @returns {Promise<void>}
     */
    async comment({ dispatch, commit, rootState }, privilegeIdLendingRecordComment: PrivilegeIdLendingRecordComment) {
        try {
            commit(types.COMMENT_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.PrivilegeIdLendingRecord.COMMENT.replace(
                        ':id',
                        privilegeIdLendingRecordComment.id.toString()
                    ),
                    params: privilegeIdLendingRecordComment,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.COMMENT_FAILURE);
            } else {
                // 成功時
                commit(types.COMMENT_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.PrivilegeIdLendingRecord.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.COMMENT_FAILURE, params);
        }
    },

    /**
     * ノードログイン記録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 特権ID貸出記録ID
     * @returns {Promise<void>}
     */
    async register_login({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.REGISTER_LOGIN_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.PrivilegeIdLendingRecord.REGISTER_LOGIN.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REGISTER_LOGIN_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REGISTER_LOGIN_FAILURE, params);
        }
    },
};
